import React, { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banknote, RefreshCw, DollarSign, TrendingDown, Wallet, Printer, FileDown } from 'lucide-react';
import DataTable from '../../components/shared/DataTable';
import { getVendorPayoutSummary, getVendorPayoutDisbursements } from '../../services/vendorPayoutService';
import { openPrintWindow, saveHtmlAsPdf, escapeHtml } from '../../utils/reportExport';
import { useToastContext } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const fmtMoney = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return `$${v.toFixed(2)}`;
};

/** Stable empty list when pagination data is missing (avoids new [] each render). */
const EMPTY_DISBURSEMENT_ROWS = [];

const VendorPayoutDisbursements = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const [page, setPage] = useState(1);
  const [pdfBusy, setPdfBusy] = useState(false);
  const perPage = 15;

  const summaryQuery = useQuery({
    queryKey: ['vendor-payout-summary'],
    queryFn: async () => getVendorPayoutSummary(),
  });

  const listQuery = useQuery({
    queryKey: ['vendor-payout-disbursements', page],
    queryFn: async () => getVendorPayoutDisbursements({ page, per_page: perPage }),
    placeholderData: (prev) => prev,
  });

  const summary = summaryQuery.data?.data;
  const pagination = listQuery.data?.data;
  const rows = useMemo(() => {
    const d = pagination?.data;
    return Array.isArray(d) ? d : EMPTY_DISBURSEMENT_ROWS;
  }, [pagination?.data]);

  const buildPrintBodyHtml = useCallback(() => {
    const vendorLine = user?.name || user?.email ? escapeHtml(user.name || user.email) : '';
    const pageNote =
      pagination && pagination.last_page > 1
        ? `Disbursements: page ${pagination.current_page} of ${pagination.last_page} (export again for other pages).`
        : '';

    const tableRows = rows
      .map(
        (r) => `<tr>
          <td>${escapeHtml(formatDt(r.created_at))}<br/><span style="font-size:10px;color:#666">${escapeHtml(r.disbursement_reference || '')}</span></td>
          <td><strong>${escapeHtml(fmtMoney(r.amount))}</strong></td>
          <td>${escapeHtml(r.booking?.booking_reference || '—')}</td>
          <td>${escapeHtml(r.notes || '—')}</td>
        </tr>`
      )
      .join('');

    return `
      <h1>Payouts from VendaList</h1>
      <p class="muted">${vendorLine ? `Vendor: ${vendorLine}<br/>` : ''}Generated ${escapeHtml(
        new Date().toLocaleString()
      )}</p>
      <div class="grid">
        <div><div class="muted">Earned from orders</div><div><strong>${escapeHtml(
          fmtMoney(summary?.total_earned_from_orders)
        )}</strong></div></div>
        <div><div class="muted">Total sent to you</div><div><strong>${escapeHtml(
          fmtMoney(summary?.total_disbursed)
        )}</strong></div></div>
        <div><div class="muted">Available balance</div><div><strong>${escapeHtml(
          fmtMoney(summary?.available_balance)
        )}</strong></div></div>
        <div><div class="muted">Net (earned − sent)</div><div><strong>${escapeHtml(
          fmtMoney(summary?.remaining_after_disbursements)
        )}</strong></div></div>
      </div>
      <h2 style="font-size:16px;margin:20px 0 8px;">Disbursement history</h2>
      <table>
        <thead><tr><th>Date / Reference</th><th>Amount sent</th><th>Booking</th><th>Notes</th></tr></thead>
        <tbody>${tableRows || '<tr><td colspan="4">No payouts on this page.</td></tr>'}</tbody>
      </table>
      ${pageNote ? `<p class="muted" style="margin-top:16px">${escapeHtml(pageNote)}</p>` : ''}
    `;
  }, [user, summary, rows, pagination]);

  const handlePrint = () => {
    const r = openPrintWindow('Payouts from VendaList', buildPrintBodyHtml());
    if (!r.ok) showError('Allow pop-ups to print this report.');
  };

  const handlePdf = async () => {
    setPdfBusy(true);
    try {
      await saveHtmlAsPdf(buildPrintBodyHtml(), `vendor-payouts-${new Date().toISOString().slice(0, 10)}`);
      success('PDF downloaded.');
    } catch (e) {
      showError(e?.message || 'Could not generate PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'when',
        label: 'Date',
        render: (row) => (
          <div className="text-sm">
            <div className="font-medium text-gray-900">{formatDt(row.created_at)}</div>
            <div className="text-xs font-mono text-gray-500 truncate max-w-[200px]" title={row.disbursement_reference}>
              {row.disbursement_reference}
            </div>
          </div>
        ),
      },
      {
        key: 'amount',
        label: 'Amount sent',
        render: (row) => (
          <span className="text-sm font-semibold text-emerald-800">{fmtMoney(row.amount)}</span>
        ),
      },
      {
        key: 'booking',
        label: 'Booking',
        render: (row) => (
          <span className="text-sm text-gray-700">{row.booking?.booking_reference || '—'}</span>
        ),
      },
      {
        key: 'notes',
        label: 'Notes',
        render: (row) => (
          <span className="text-sm text-gray-600 line-clamp-2 max-w-xs">{row.notes || '—'}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
     <div className="space-y-3 sm:space-y-4">
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Banknote className="w-8 h-8 text-primary-600" />
            Payouts from VendaList
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl">
            Amounts the platform has recorded as sent to you for order earnings. Your remaining balance is earned
            from completed client payments (after fees) minus these payouts.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4 sm:mt-0">
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </button>
          <button
            type="button"
            onClick={handlePdf}
            disabled={pdfBusy || listQuery.isLoading}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {pdfBusy ? 'PDF…' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={() => {
              summaryQuery.refetch();
              listQuery.refetch();
            }}
            disabled={summaryQuery.isFetching || listQuery.isFetching}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${summaryQuery.isFetching || listQuery.isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {summaryQuery.error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">
          {summaryQuery.error.message || 'Could not load balance summary.'}
        </div>
      )}

      <div className="space-y-6 bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
        <div className="border-b border-gray-100 pb-4">
          <p className="text-xl font-bold text-gray-900">Payouts from VendaList</p>
          {(user?.name || user?.email) && (
            <p className="text-sm text-gray-600 mt-1">{user.name || user.email}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">{new Date().toLocaleString()}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 sm:bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-50 text-primary-700">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Earned from orders</p>
              <p className="text-lg font-bold text-gray-900">{fmtMoney(summary?.total_earned_from_orders)}</p>
            </div>
          </div>
          <div className="bg-gray-50 sm:bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-800">
              <TrendingDown className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total sent to you</p>
              <p className="text-lg font-bold text-gray-900">{fmtMoney(summary?.total_disbursed)}</p>
            </div>
          </div>
          <div className="bg-gray-50 sm:bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-800">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Available balance</p>
              <p className="text-lg font-bold text-emerald-900">{fmtMoney(summary?.available_balance)}</p>
            </div>
          </div>
          <div className="bg-gray-50 sm:bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 text-gray-700">
              <Banknote className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Net (earned − sent)</p>
              <p
                className={`text-lg font-bold ${
                  (summary?.remaining_after_disbursements ?? 0) < 0 ? 'text-red-700' : 'text-gray-900'
                }`}
              >
                {fmtMoney(summary?.remaining_after_disbursements)}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 pt-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Disbursement history</h2>
            {pagination && pagination.last_page > 1 && (
              <p className="text-xs text-gray-500">
                Showing page {pagination.current_page} of {pagination.last_page}. Print and PDF include this page
                only; switch pages to capture the rest.
              </p>
            )}
          </div>
          {listQuery.error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">
              {listQuery.error.message || 'Could not load disbursements.'}
            </div>
          )}
          <DataTable
            columns={columns}
            data={rows}
            loading={listQuery.isLoading && !listQuery.data}
            searchable={false}
            filterable={false}
            sortable={false}
            showPagination={false}
            emptyMessage="No payouts recorded yet."
          />
          {pagination && pagination.last_page > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100 print:hidden">
              <p className="text-sm text-gray-600">
                Page {pagination.current_page} of {pagination.last_page}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={pagination.current_page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={pagination.current_page >= pagination.last_page}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
    </div>
  );
};

function formatDt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default VendorPayoutDisbursements;
